import { captureException } from '@sentry/browser';
import { GlobalMetaTagName, InitialStateConfig, type MetaTagName } from './state-config';

const metaTagCache = new Map<string, unknown>();

/**
 * Get injected content from meta tags or cache (if pre-loaded via API)
 */
export function getInjectedContent<T>(name: MetaTagName): T | null {
  if (metaTagCache.has(name)) {
    return metaTagCache.get(name) as T;
  }

  // Always try to read from meta tag HTML (for backward compatibility during migration)
  try {
    const content = document.querySelector(`meta[name='${name}']`)?.getAttribute('content')?.trim();

    if (!content) {
      metaTagCache.set(name, null);
      return null;
    }

    const isEncoded = InitialStateConfig[name];
    const result = isEncoded ? (JSON.parse(globalThis.atob(content)) as T) : (content as T);

    metaTagCache.set(name, result);
    return result;
  } catch (err) {
    captureException(new Error(`Error while parsing initial content meta tag ${name}`, { cause: err }));
    metaTagCache.set(name, null);
    return null;
  }
}

/**
 * Check if meta tag migration is enabled for a specific state via feature flag (fc-1327-meta-{name}).
 * Reads features fresh every call via getInjectedContent so late hydration (API or meta tag)
 * is picked up — previously a module-level cache snapshot could freeze to {} if called before
 * setInjectedContent(Features, ...) ran, masking later updates.
 */
export function isMetaTagMigrationEnabled(name: string): boolean {
  const flags = getInjectedContent<Record<string, boolean | string>>(GlobalMetaTagName.Features) ?? {};
  const normalizedName = name.replace(/\./g, '-');
  const value = flags[`fc-1327-meta-${normalizedName}`];
  return value === 'on' || value === true;
}

/**
 * Set injected content directly (for API-loaded data)
 */
export function setInjectedContent<T>(name: MetaTagName, value: T): void {
  metaTagCache.set(name, value);
}

/**
 * Clear caches (for testing)
 */
export function clearCache(): void {
  metaTagCache.clear();
}
